/**
 * useTenantVehicles - Listagem de TODOS os veículos do tenant (página "Lista Veículos")
 *
 * Toda leitura passa por RPCs SECURITY DEFINER (nunca SELECT direto do frontend).
 * A listagem e os KPIs usam exatamente os mesmos filtros, garantindo números
 * sincronizados entre os cards e a tabela.
 *
 * @version 1.2.0
 * RELEASE: 2026-10-03 v1.2.0 - Expõe UF e cidade do cliente vinculado para
 *          os recortes auditáveis do mapa do Dashboard.
 * RELEASE: 2026-10-03 v1.1.1 - Corrige a chamada RPC sem vínculo ao cliente
 *          (perdia o `this` e nunca chegava ao servidor).
 * RELEASE: 2026-10-03 v1.1.0 - Expõe filtros opcionais do detalhamento do
 *          Dashboard de Veículos e metadados das movimentações.
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { VehicleType } from '@/hooks/useCustomerVehicles';

const logger = createLogger('useTenantVehicles');

/** Assinatura mínima para RPCs recém-versionadas enquanto os tipos são regenerados. */
type RpcInvoker = (
  functionName: string,
  parameters: Record<string, unknown>,
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

// v1.1.1: `bind` preserva o `this` do cliente; sem ele o Supabase falha antes de enviar a requisição.
const invokeRpc = supabase.rpc.bind(supabase) as unknown as RpcInvoker;

/** Veículo retornado por list_tenant_vehicles (inclui o vínculo) */
export interface TenantVehicle {
  id: string;
  vehicle_type: VehicleType;
  category_id: string | null;
  category_name: string | null;
  brand_id: string | null;
  brand_name: string | null;
  model_id: string | null;
  model_name: string | null;
  year_code: string | null;
  year: number | null;
  fuel: string | null;
  color: string | null;
  plate: string | null;
  renavam: string | null;
  chassis: string | null;
  fipe_code: string | null;
  fipe_value: number | null;
  notes: string | null;
  customer_id: string | null;
  customer_name: string | null;
  contact_id: string | null;
  contact_name: string | null;
  /** Fase 4 (Multas): total em aberto e data da última sincronização */
  fines_total_open: number | null;
  fines_synced_at: string | null;
  /** 2026-10-02: status do veículo */
  status_id: string | null;
  created_at: string;
  /** Data e tipo da ocorrência quando a listagem representa movimentações. */
  activity_at: string | null;
  activity_action: string | null;
}

/** KPIs da listagem */
export interface TenantVehiclesKpis {
  total: number;
  cars: number;
  motorcycles: number;
  trucks: number;
  others: number;
  fipe_total: number;
  /** Fase 4 (Multas) */
  fines_open_total: number;
  fines_vehicles: number;
}

/** Filtros aplicados à listagem e aos KPIs */
export interface TenantVehiclesFilters {
  search?: string | null;
  vehicleType?: VehicleType | null;
  categoryId?: string | null;
  /** 2026-10-02: filtro por status */
  statusId?: string | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  fipeOnly?: boolean;
  yearMin?: number | null;
  yearMax?: number | null;
  brandNames?: string[] | null;
  excludeBrandNames?: boolean;
  modelNames?: string[] | null;
  excludeModelNames?: boolean;
  customerId?: string | null;
  statusBase?: 'active' | 'cancelled' | null;
  eventAction?: 'vehicle_transferred' | 'vehicle_inactivated' | null;
  eventFrom?: string | null;
  eventTo?: string | null;
  customerStates?: string[] | null;
  customerCity?: string | null;
  page?: number;
  pageSize?: number;
}

/**
 * Lista paginada + KPIs dos veículos do tenant.
 */
export function useTenantVehicles(filters: TenantVehiclesFilters) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const search = filters.search?.trim() || null;
  const vehicleType = filters.vehicleType ?? null;
  const categoryId = filters.categoryId ?? null;
  const statusId = filters.statusId ?? null;
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 25;

  const listQuery = useQuery({
    queryKey: ['tenant-vehicles', tenantId, search, vehicleType, categoryId, statusId,
      filters.createdFrom, filters.createdTo, filters.fipeOnly, filters.yearMin, filters.yearMax,
      filters.brandNames, filters.excludeBrandNames, filters.modelNames, filters.excludeModelNames,
      filters.customerId, filters.statusBase, filters.eventAction, filters.eventFrom, filters.eventTo,
      filters.customerStates, filters.customerCity,
      page, pageSize],
    enabled: !!tenantId,
    staleTime: 30_000,
    queryFn: async (): Promise<{ items: TenantVehicle[]; total: number }> => {
      const { data, error } = await invokeRpc('list_tenant_vehicles', {
        p_search: search,
        p_vehicle_type: vehicleType,
        p_category_id: categoryId,
        p_page: page,
        p_page_size: pageSize,
        p_status_id: statusId,
        p_created_from: filters.createdFrom ?? null,
        p_created_to: filters.createdTo ?? null,
        p_fipe_only: filters.fipeOnly ?? false,
        p_year_min: filters.yearMin ?? null,
        p_year_max: filters.yearMax ?? null,
        p_brand_names: filters.brandNames ?? null,
        p_exclude_brand_names: filters.excludeBrandNames ?? false,
        p_model_names: filters.modelNames ?? null,
        p_exclude_model_names: filters.excludeModelNames ?? false,
        p_customer_id: filters.customerId ?? null,
        p_status_base: filters.statusBase ?? null,
        p_event_action: filters.eventAction ?? null,
        p_event_from: filters.eventFrom ?? null,
        p_event_to: filters.eventTo ?? null,
        p_customer_states: filters.customerStates ?? null,
        p_customer_city: filters.customerCity ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar veículos do tenant', { error: error.message });
        throw error;
      }
      const payload = (data || {}) as { items?: TenantVehicle[]; total_count?: number };
      return { items: payload.items ?? [], total: Number(payload.total_count ?? 0) };
    },
  });

  const kpisQuery = useQuery({
    queryKey: ['tenant-vehicles-kpis', tenantId, search, vehicleType, categoryId],
    enabled: !!tenantId,
    staleTime: 30_000,
    queryFn: async (): Promise<TenantVehiclesKpis> => {
      const { data, error } = await invokeRpc('get_tenant_vehicles_kpis', {
        p_search: search,
        p_vehicle_type: vehicleType,
        p_category_id: categoryId,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar KPIs de veículos', { error: error.message });
        throw error;
      }
      const payload = (data || {}) as Partial<TenantVehiclesKpis>;
      return {
        total: Number(payload.total ?? 0),
        cars: Number(payload.cars ?? 0),
        motorcycles: Number(payload.motorcycles ?? 0),
        trucks: Number(payload.trucks ?? 0),
        others: Number(payload.others ?? 0),
        fipe_total: Number(payload.fipe_total ?? 0),
        fines_open_total: Number(payload.fines_open_total ?? 0),
        fines_vehicles: Number(payload.fines_vehicles ?? 0),
      };
    },
  });

  return {
    vehicles: listQuery.data?.items ?? [],
    total: listQuery.data?.total ?? 0,
    kpis: kpisQuery.data,
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching || kpisQuery.isFetching,
    refetch: () => {
      listQuery.refetch();
      kpisQuery.refetch();
    },
  };
}
